// src/redux/services/axiosBaseQuery.ts

import type { BaseQueryFn } from '@reduxjs/toolkit/query';

import type { AxiosRequestConfig, Method } from 'axios';

import axiosInstance from '../../services/axios/axios';

export interface AxiosBaseQueryArgs {
  url: string;

  method?: Method;

  /**
   * Both names are supported during migration.
   *
   * RTK Query endpoints normally use `body`.
   * Older Axios-based endpoints may use `data`.
   */
  data?: unknown;
  body?: unknown;

  params?: AxiosRequestConfig['params'];
  headers?: AxiosRequestConfig['headers'];

  retryUnsafeRequest?: boolean;
}

export interface AxiosBaseQueryError {
  status?: number;
  data?: unknown;
  message: string;
}

interface AxiosBaseQueryConfig {
  baseUrl?: string;
}

const axiosBaseQuery = ({ baseUrl = '' }: AxiosBaseQueryConfig = {}): BaseQueryFn<
  AxiosBaseQueryArgs,
  unknown,
  AxiosBaseQueryError
> => {
  return async ({ url, method = 'GET', data, body, params, headers, retryUnsafeRequest = false }) => {
    try {
      const result = await axiosInstance({
        url: `${baseUrl}${url}`,
        method,
        data: data ?? body,
        params,
        headers,

        /*
         * Custom property used by your Axios logic.
         * The cast is needed because it is not part
         * of AxiosRequestConfig by default.
         */
        __retryUnsafeRequest: retryUnsafeRequest,
      } as AxiosRequestConfig & {
        __retryUnsafeRequest?: boolean;
      });

      return {
        data: result.data,
      };
    } catch (rawError: unknown) {
      const error = rawError as {
        status?: number;
        data?: unknown;
        message?: string;

        response?: {
          status?: number;
          data?: unknown;
        };
      };

      return {
        error: {
          status: error.status ?? error.response?.status,

          data: error.data ?? error.response?.data,

          message: error.message ?? 'API request failed',
        },
      };
    }
  };
};

export default axiosBaseQuery;
